'use client';
import { useEffect } from 'react';
import { Box, Stack, Typography, useTheme } from '@mui/material';

import { ScreenContainer } from 'src/components/flow';
import LoadingN from 'src/components/loading-n';
import { terminateQuizFlow } from 'src/redux/actions/terminate-quiz-flow';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import type { FlowScreenProps } from './types';
import {
  FormName,
  EventType,
  PageActionName,
  PageActionType,
} from 'src/utils/analytics/constants';
import { v4 as uuid } from 'uuid';
import { useSaveUserData } from 'src/utils/user-data/hooks';
import { getMethodology } from 'src/lib/recommendations/utils';
import {
  getAuth0State,
  getExperiments,
  getUserEmail,
  getVisitedScreens,
} from 'src/redux/selectors';
import { getActiveFlow } from 'src/redux/actions/flow';
import {
  getRelevantInputs,
  gtmPushToDataLayer,
} from 'src/utils/analytics/utils';
import { buildResultsUrl } from 'src/redux/actions/terminate-quiz-flow';
import { useMutation } from '@apollo/client';
import { RegisterDocument } from 'src/generated/graphql';
import { passwordRegexEval } from './registration/password-requirements';
import { client } from 'src/utils/apollo-client';
import { usePassword } from '../providers/password';
import { useAnonymousId, useCallbackEvent } from 'src/utils/analytics/hooks';
import { datadogRum } from '@datadog/browser-rum';
import { useSegmentPageAction } from 'src/utils/analytics/hooks';
import { CCIN_AUTH0 } from 'src/lib/experiments';
import { Routes } from 'src/lib/constants';

const SITE_HEADER_HEIGHT = '96px';
const LOADING_TIMEOUT = 10000;

const setMemberToken = (memberToken: string) => {
  const oneYearFromNow = new Date();
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
  const expires = `expires=${oneYearFromNow}`;
  document.cookie = `member_identity_assertion_token=${memberToken}; ${expires}; path=/`;
};

const Loading: React.FC<FlowScreenProps> = ({
  inputs,
  trackEvent,
  formImpressionId: impressionId,
  sectionName,
}) => {
  const dispatch = useAppDispatch();
  const saveUserData = useSaveUserData();
  const experiments = useAppSelector(getExperiments);
  const activeFlow = dispatch(getActiveFlow());
  const visitedScreens = useAppSelector(getVisitedScreens);
  const signUpAnalytics = useAppSelector((state) => state.userData.analytics);
  const anonymousId = useAnonymousId();
  const [mutate, { error }] = useMutation(RegisterDocument, {
    client,
  });
  const email = useAppSelector(getUserEmail);
  const { password } = usePassword();
  const isPasswordValid = !!password && passwordRegexEval(password);
  const trackMembershipEvent = useCallbackEvent({});
  const trackPageAction = useSegmentPageAction();
  const auth0State = useAppSelector(getAuth0State);

  useEffect(() => {
    if (!anonymousId) return;
    const prepForRedirect = async () => {
      const formSubmitId = uuid();

      const resultsDeepLink = dispatch(
        buildResultsUrl({
          formSubmitId,
          saveInputResponse: false, //forcing this to generate the entire link
          inputsOverride: inputs,
        })
      );

      if (isPasswordValid) {
        try {
          if (signUpAnalytics === undefined) {
            datadogRum.addError(
              new Error(
                'Unable to setup signup analytics properly, signup analytics is null'
              )
            );
          }
          const result = await mutate({
            variables: {
              email: email!,
              password,
              trackingProfile: {
                trackingCode: 'cardfinder_recommendations',
                location: 'cardfinder',
                vertical: 'credit_cards',
                impressionID: signUpAnalytics?.pageImpressionID ?? impressionId,
                pageName: signUpAnalytics?.pageName ?? sectionName,
                anonymousId,
              },
            },
          });
          if (
            result?.data?.cardFinderRegister?.success &&
            result?.data?.cardFinderRegister?.memberIdentityAssertionToken
          ) {
            setMemberToken(
              result?.data?.cardFinderRegister.memberIdentityAssertionToken
            );
            trackMembershipEvent(EventType.MembershipEnrollmentChanged, {
              enrollment_change_id: uuid(),
              feature_name: 'membership',
              enrollment_change_type: 'enroll',
              additional_attributes: {
                authentication_provider: 'NERDWALLET',
                cohort: 'cardfinder_recommendations',
              },
            });
          } else {
            datadogRum.addError(new Error('Failed to register user'), {
              registrationSuccessful: result?.data?.cardFinderRegister?.success,
              memberIdentityAssertionToken:
                !!result?.data?.cardFinderRegister
                  ?.memberIdentityAssertionToken,
            });
          }
        } catch (e) {
          console.error(
            new Error('failed to register user', {
              cause: e,
            })
          );
        }
      }
      if (error) {
        console.error(error);
      }

      const saveInputResponse = await saveUserData(inputs);

      console.log(`saveInputResponse in <Loading/>: `, saveInputResponse);

      try {
        trackPageAction({
          actionName: PageActionName.CcRecsSubmit,
          actionType: PageActionType.Submit,
          actionInput: null,
          activityId: formSubmitId,
          additionalAttributes: {
            results_deep_link: resultsDeepLink,
            methodology: getMethodology(inputs, experiments).toLowerCase(),
            inputs: Object.entries(
              getRelevantInputs(inputs, visitedScreens, activeFlow)
            ),
          },
        });
      } catch (e) {
        console.log(
          `Error tracking segment event ${PageActionName.CcRecsSubmit}`,
          e
        );
      }

      try {
        await trackEvent(EventType.CcRecsSubmit, {
          form_name: FormName.Flow,
          additional_attributes: {
            methodology: getMethodology(inputs, experiments),
            submit_id: formSubmitId,
            inputs: JSON.stringify(
              getRelevantInputs(inputs, visitedScreens, activeFlow)
            ),
            results_deep_link: new URL(
              `${window.location.protocol}//${window.location.host}${resultsDeepLink}`
            ),
          },
        });
      } catch (e) {
        console.log(`Error tracking event ${EventType.CcRecsSubmit}`, e);
      }

      if (auth0State) {
        if (auth0State === CCIN_AUTH0.variant.CONTROL) {
          gtmPushToDataLayer({ event: 'formcomplete' });
        }
        if (resultsDeepLink) window.location.assign(resultsDeepLink);
      } else {
        dispatch(terminateQuizFlow(formSubmitId, saveInputResponse));
      }
    };

    const parent = window.opener;
    if (parent && auth0State === `${CCIN_AUTH0.variant.VARIANT}_child`) {
      parent.window?.location.assign(`${window?.location}`);
      window?.close();
    } else {
      prepForRedirect();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anonymousId]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      datadogRum.addError(
        new Error('Cardfinder loading screen timeouted after 10s')
      );
      console.error('Cardfinder loading screen timeouted after 10s');
      window.location.assign(Routes.Results);
    }, LOADING_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const theme = useTheme();

  return (
    <ScreenContainer>
      <Box
        sx={{
          [theme.breakpoints.down('sm')]: {
            minHeight: `calc(100vh - ${SITE_HEADER_HEIGHT})`, // TODO is this needed? will 100% work?
          },
          [theme.breakpoints.up('sm')]: {
            marginTop: '25vh',
          },
          display: 'flex',
          alignItem: 'center',
          justifyContent: 'center',
        }}
      >
        <Stack spacing={3}>
          <Box display="flex" justifyContent="center">
            <LoadingN size={65} />
          </Box>
          <Box display="flex" justifyContent="center">
            <Typography component="h1" align="center" variant="titleSmall">
              Pulling up your credit card matches...
            </Typography>
          </Box>
        </Stack>
      </Box>
    </ScreenContainer>
  );
};

export default Loading;
